import { useRef } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Ship, Plane, Truck, Star } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import Reveal from "../components/site/Reveal";
import Seo, { ORG_JSONLD, SITE_URL } from "../components/site/Seo";
import { DIVISIONS, IMAGES } from "../lib/site-data";

const HOME_JSONLD = [
  ORG_JSONLD,
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Ocean Oasis",
    url: SITE_URL,
    inLanguage: "en",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const heroFade = useTransform(scrollYProgress, [0, 0.85], [1, 0.25]);

  return (
    <main data-testid="page-home" className="overflow-hidden">
      <Seo
        title="Ocean Oasis | Supermarket, Logistics & Coffee in Mogadishu, Somalia"
        description="Ocean Oasis is a Somali-owned group at Sundus Plaza, Bondhere District, Mogadishu — a premium supermarket, an international logistics company and a specialty coffee house."
        path="/"
        jsonLd={HOME_JSONLD}
      />

      {/* HERO */}
      <section
        ref={heroRef}
        className="relative min-h-[100svh] flex items-end pt-32 pb-16 md:pb-28 bg-[#0a3d7a]"
      >
        {/* Parallax background */}
        <motion.div style={{ opacity: heroFade }} className="absolute inset-0 z-0 overflow-hidden">
          <motion.img
            src={IMAGES.heroSkyline}
            alt="Mogadishu coastline at sunset — home of the Ocean Oasis Group"
            fetchPriority="high"
            style={{ y: heroY, scale: 1.12 }}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a3d7a]/30 via-[#0a3d7a]/55 to-[#0a3d7a]/95" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a3d7a]/55 via-transparent to-transparent" />
        </motion.div>

        {/* Group identifier */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="absolute top-28 md:top-32 right-6 md:right-10 lg:right-16 z-10 hidden sm:block text-right"
        >
          <p className="font-label text-[#f59e0b] text-[0.62rem]">The Group</p>
          <p className="font-display text-[#faf6f0] text-lg md:text-xl mt-1 leading-tight">
            Three Divisions <span className="text-[#f59e0b]">·</span> One Standard
          </p>
        </motion.div>

        <div className="container-x relative z-10 w-full">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-9">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="font-label text-[#f59e0b] mb-6 flex items-center gap-3"
              >
                <span className="w-10 h-px bg-[#f59e0b]" />
                Mogadishu · Est. Ocean Oasis Group
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="font-display text-[#faf6f0] leading-[0.92] tracking-tight"
                style={{ fontSize: "clamp(2.75rem, 9vw, 8rem)" }}
              >
                Connecting <em className="italic text-[#f59e0b]">Somalia</em>
                <br />
                to the world.
              </motion.h1>
            </div>
            <div className="lg:col-span-3 lg:pb-3">
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="font-body text-[#faf6f0]/85 text-base leading-relaxed border-l-2 border-[#f59e0b] pl-5"
              >
                A Somali-owned conglomerate of three world-class divisions —
                premium retail, international logistics, and an oasis for those
                who pause.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="mt-6 flex flex-wrap gap-3"
              >
                <Link
                  to="/logistics"
                  data-testid="hero-cta-logistics"
                  className="btn-primary"
                  style={{ background: "#f59e0b", color: "#0a3d7a" }}
                >
                  Explore Divisions
                </Link>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2">
          <span className="font-label text-[#faf6f0]/70 text-[0.6rem]">Scroll</span>
          <div className="w-px h-12 bg-[#faf6f0]/40 relative overflow-hidden">
            <motion.div
              animate={{ y: [-48, 48] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-x-0 top-0 h-6 bg-[#f59e0b]"
            />
          </div>
        </div>
      </section>

      {/* Marquee strip */}
      <div className="marquee-strip" aria-hidden="true">
        <Marquee speed={42} gradient={false} pauseOnHover>
          {[
            "Connecting Somalia to the World",
            "From Land to Sea to Sky",
            "Premium Quality, Daily Abundance",
            "Your Oasis of Relaxation",
            "Delivering Beyond Borders",
            "Mogadishu · Somalia",
            "Trusted Cargo Solutions Worldwide",
          ].map((t, i) => (
            <span
              key={i}
              className="font-display italic text-2xl md:text-3xl mx-10 text-[#faf6f0]"
            >
              {t} <span className="text-[#f59e0b] mx-3">✦</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* INTRO / ABOUT SNIPPET */}
      <section className="py-24 md:py-32 bg-[#faf6f0]">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-4">
              <Reveal>
                <p className="font-label text-[#f59e0b]">The Group</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-5 leading-[1.02] text-[#0a3d7a]">
                  One name. <br />
                  <em className="italic">Three oases.</em>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7 lg:col-start-6 space-y-7">
              <Reveal delay={0.1}>
                <p className="font-body text-lg md:text-xl text-[#0a3d7a]/85 leading-relaxed">
                  Ocean Oasis is a Somali-owned conglomerate built in
                  Mogadishu — engineered to bring world-class standards to
                  everyday Somali life and to carry Somali business out into
                  the world.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="font-body text-base text-[#0a3d7a]/70 leading-relaxed">
                  From a single trusted address at Sundus Plaza in Bondhere
                  District, we operate three distinct experiences — a premium
                  supermarket, a globally connected logistics business, and a
                  refined coffee house. Each held to the same uncompromising
                  standard: <em className="italic font-display text-[#0a3d7a] text-lg">excellence as a default.</em>
                </p>
              </Reveal>
              <Reveal delay={0.3}>
                <div className="flex flex-wrap items-center gap-x-10 gap-y-4 pt-4">
                  <Stat n="3" label="Divisions" />
                  <Stat n="∞" label="Worldwide Sourcing" />
                  <Stat n="24/7" label="Operations" />
                  <Stat n="1°" label="Standard — Premium" />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* DIVISIONS SHOWCASE — Bento grid */}
      <section className="py-24 md:py-32 bg-[#fdf9f2] relative">
        <div className="container-x">
          <Reveal>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
              <div>
                <p className="font-label text-[#f59e0b]">Our Divisions</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 text-[#0a3d7a] leading-tight">
                  Pick your oasis.
                </h2>
              </div>
              <p className="font-body text-base text-[#0a3d7a]/70 max-w-md">
                Three businesses, one philosophy. Each division is built to be
                the very best of its kind in Somalia — and a serious player
                beyond it.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            <Reveal className="md:col-span-7 lg:col-span-8">
              <DivisionCard d={DIVISIONS[0]} variant="large" />
            </Reveal>
            <Reveal delay={0.12} className="md:col-span-5 lg:col-span-4">
              <DivisionCard d={DIVISIONS[1]} variant="tall" />
            </Reveal>
            <Reveal delay={0.2} className="md:col-span-12">
              <DivisionCard d={DIVISIONS[2]} variant="wide" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* LOGISTICS TEASER STRIP */}
      <section className="bg-[#0a3d7a] text-[#faf6f0] py-20 md:py-28">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <p className="font-label text-[#f59e0b] mb-5">
                  Ocean Oasis Logistics
                </p>
                <h3 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
                  From land to sea to sky — <em className="italic">delivered.</em>
                </h3>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <p className="font-body text-[#faf6f0]/80 text-base leading-relaxed mb-8">
                  Integrated multi-modal transportation, customs clearance,
                  warehousing and distribution — purpose-built for the
                  realities of Somali trade and the demands of global commerce.
                </p>
                <div className="flex flex-wrap gap-6 mb-8">
                  {[
                    { Icon: Ship, label: "Ocean" },
                    { Icon: Plane, label: "Air" },
                    { Icon: Truck, label: "Land" },
                  ].map(({ Icon, label }) => (
                    <div
                      key={label}
                      className="flex items-center gap-2 text-[#faf6f0]"
                    >
                      <Icon size={20} className="text-[#f59e0b]" />
                      <span className="font-label text-[#faf6f0]">{label}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to="/logistics"
                  data-testid="home-logistics-cta"
                  className="btn-primary"
                  style={{ background: "#f59e0b", color: "#0a3d7a" }}
                >
                  Discover Logistics <ArrowUpRight size={16} />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST / VALUES */}
      <section className="py-24 md:py-32 bg-[#faf6f0]">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="font-label text-[#f59e0b]">Why Ocean Oasis</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-4 text-[#0a3d7a] leading-tight">
                  Built on <em className="italic">trust.</em>
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <Reveal delay={0.1}>
                <p className="font-body text-lg text-[#0a3d7a]/80 leading-relaxed">
                  We don't chase trends. We chase reliability. Across every
                  division, our promise is consistency — the kind of
                  consistency that international clients, families, and
                  visitors can depend on without question.
                </p>
              </Reveal>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[rgba(10,61,122,0.15)] border border-[rgba(10,61,122,0.15)]">
            {[
              { n: "01", t: "Reliability", d: "Operations you can plan around." },
              { n: "02", t: "Integrity", d: "Transparent, accountable conduct in every transaction." },
              { n: "03", t: "Excellence", d: "Premium standard maintained as the baseline." },
              { n: "04", t: "Global Reach", d: "Local expertise, international connectivity." },
            ].map((v, i) => (
              <Reveal
                key={v.n}
                delay={i * 0.08}
                className="bg-[#fdf9f2] p-6 md:p-10"
              >
                <p className="font-label text-[#f59e0b] mb-3">{v.n}</p>
                <p className="font-display text-2xl md:text-3xl text-[#0a3d7a] mb-3">
                  {v.t}
                </p>
                <p className="font-body text-sm text-[#0a3d7a]/70 leading-relaxed">
                  {v.d}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL / QUOTE */}
      <section className="py-24 md:py-32 bg-[#fdf9f2]">
        <div className="container-x">
          <div className="max-w-4xl">
            <Reveal>
              <div className="flex gap-1 mb-6 text-[#f59e0b]">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={16} fill="#f59e0b" />
                ))}
              </div>
              <blockquote className="font-display italic text-3xl md:text-5xl lg:text-6xl text-[#0a3d7a] leading-[1.08]">
                "Ocean Oasis is doing what Somalia has waited for —
                world-class everyday standards, delivered by people who
                actually live here."
              </blockquote>
              <p className="font-label text-[#0a3d7a]/70 mt-8">
                — A guiding principle, not a slogan
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA - VISIT */}
      <section className="py-24 md:py-32 bg-[#faf6f0]">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <p className="font-label text-[#f59e0b] mb-5">Find us</p>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#0a3d7a] leading-[1.02]">
                  One address. <em className="italic">All three oases.</em>
                </h2>
                <p className="font-body text-lg text-[#0a3d7a]/75 leading-relaxed mt-6 max-w-xl">
                  Sundus Plaza, Bondhere District — the home of Ocean Oasis
                  Supermarket, Ocean Oasis Coffee, and the headquarters of
                  Ocean Oasis Logistics.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-4">
              <Reveal delay={0.1}>
                <Link
                  to="/contact"
                  data-testid="home-cta-contact"
                  className="btn-primary"
                >
                  Visit & Contact
                </Link>
              </Reveal>
              <Reveal delay={0.2}>
                <Link
                  to="/about"
                  data-testid="home-cta-about"
                  className="btn-outline"
                >
                  About the Group
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <p className="font-display text-4xl md:text-5xl text-[#0a3d7a] leading-none">
        {n}
      </p>
      <p className="font-label text-[#0a3d7a]/60 mt-2">{label}</p>
    </div>
  );
}

function CardBody({ d, compact = false }) {
  return (
    <div className={`px-6 md:px-10 pt-8 pb-10 ${compact ? "md:flex md:flex-col md:justify-center md:py-10" : ""}`}>
      <div className="w-28 h-28 md:w-32 md:h-32 mb-4 flex items-center justify-start">
        <img
          src={d.logo}
          alt={`${d.name} logo`}
          loading="lazy"
          decoding="async"
          className="max-w-full max-h-full w-auto h-auto object-contain object-left mix-blend-multiply"
        />
      </div>
      <p className="font-display italic text-lg md:text-xl text-[#f59e0b]">
        {d.tagline}
      </p>
      <p className="font-body text-sm md:text-base text-[#0a3d7a]/70 leading-relaxed mt-5 max-w-xl">
        {d.blurb}
      </p>
      <span className="mt-7 inline-flex items-center gap-2 font-label text-[#0a3d7a] group-hover:text-[#f59e0b] transition-colors">
        Discover {d.short}
        <ArrowUpRight
          size={16}
          className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
        />
      </span>
    </div>
  );
}

function CardImage({ d, className }) {
  return (
    <div className={`img-zoom relative overflow-hidden bg-[#0a3d7a] ${className}`}>
      <img
        src={d.image}
        alt={`${d.name} — ${d.tagline}`}
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a3d7a]/55 via-transparent to-transparent" />
      <p className="absolute top-5 left-5 font-label text-[#faf6f0] bg-[#0a3d7a]/55 backdrop-blur-sm px-3 py-1.5">
        {d.label}
      </p>
    </div>
  );
}

function DivisionCard({ d, variant = "large" }) {
  const shell =
    "group block relative h-full bg-[#fdf9f2] border border-[rgba(10,61,122,0.12)] overflow-hidden transition-[transform,box-shadow] duration-700 hover:-translate-y-1 hover:shadow-[0_30px_80px_-30px_rgba(10,61,122,0.35)]";

  if (variant === "wide") {
    return (
      <Link to={d.href} data-testid={`division-card-${d.slug}`} className={shell}>
        <div className="grid md:grid-cols-2 h-full">
          <CardImage d={d} className="aspect-[16/10] md:aspect-auto md:min-h-[380px]" />
          <CardBody d={d} compact />
        </div>
      </Link>
    );
  }

  return (
    <Link to={d.href} data-testid={`division-card-${d.slug}`} className={shell}>
      <CardImage
        d={d}
        className={variant === "large" ? "aspect-[16/9]" : "aspect-[16/10] lg:aspect-[4/3]"}
      />
      <CardBody d={d} />
    </Link>
  );
}
